import { lazy } from "react";

import { RoutePathBuilder } from "@/common/builders/routePath";
import AuthorizedRoute from "@/common/components/Auth/AuthorizedRoute";
import { Loadable } from "@/common/components/Loadable";
import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission } from "@/core/api/generated/v1";

const AccessoryCheckCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/AccessoryChecks/AccessoryCheckCreateUpdatePage")),
);
const AccessoryCheckViewPage = Loadable(
  lazy(() => import("../../MainAppView/AccessoryChecks/AccessoryCheckViewPage")),
);
const AccessoryChecksPage = Loadable(
  lazy(() => import("../../MainAppView/AccessoryChecks/AccessoryChecksPage")),
);
const VisualInspectionCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/VisualInspection/VisualInspectionCreateUpdatePage")),
);
const VisualInspectionViewPage = Loadable(
  lazy(() => import("../../MainAppView/VisualInspection/VisualInspectionViewPage")),
);
const VisualInspectionsPage = Loadable(
  lazy(() => import("../../MainAppView/VisualInspection/VisualInspectionsPage")),
);
const WashCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Washes/WashCreateUpdatePage")),
);
const WashViewPage = Loadable(lazy(() => import("../../MainAppView/Washes/WashViewPage")));
const WashesPage = Loadable(lazy(() => import("../../MainAppView/Washes/WashesPage")));
const WheelOperationCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/WheelOperation/WheelOperationCreateUpdatePage")),
);
const WheelOperationViewPage = Loadable(
  lazy(() => import("../../MainAppView/WheelOperation/WheelOperationViewPage")),
);
const WheelOperationsPage = Loadable(
  lazy(() => import("../../MainAppView/WheelOperation/WheelOperationsPage")),
);
const DamageCostEvaluationAggregateViewPage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/DamageCostEvaluationAggregates/DamageCostEvaluationAggregateViewPage"),
  ),
);
const DamageCostEvaluationAggregatesPage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/DamageCostEvaluationAggregates/DamageCostEvaluationAggregatesPage"),
  ),
);
const DamageCostEvaluationCreateUpdatePage = Loadable(
  lazy(
    () => import("../../MainAppView/DamageCostEvaluations/DamageCostEvaluationCreateUpdatePage"),
  ),
);
const DamageCostEvaluationViewPage = Loadable(
  lazy(() => import("../../MainAppView/DamageCostEvaluations/DamageCostEvaluationViewPage")),
);
const DamageCostEvaluationsPage = Loadable(
  lazy(() => import("../../MainAppView/DamageCostEvaluations/DamageCostEvaluationsPage")),
);
const DamageDetectionAggregateViewPage = Loadable(
  lazy(
    () => import("../../MainAppView/DamageDetectionAggregates/DamageDetectionAggregateViewPage"),
  ),
);
const DamageDetectionAggregatesPage = Loadable(
  lazy(() => import("../../MainAppView/DamageDetectionAggregates/DamageDetectionAggregatesPage")),
);
const DamageDetectionCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/DamageDetections/DamageDetectionCreateUpdatePage")),
);
const DamageDetectionViewPage = Loadable(
  lazy(() => import("../../MainAppView/DamageDetections/DamageDetectionViewPage")),
);
const DamageDetectionsPage = Loadable(
  lazy(() => import("../../MainAppView/DamageDetections/DamageDetectionsPage")),
);
const MaintenanceCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Maintenances/MaintenanceCreateUpdatePage")),
);
const MaintenanceViewPage = Loadable(
  lazy(() => import("../../MainAppView/Maintenances/MaintenanceViewPage")),
);
const MaintenancesPage = Loadable(
  lazy(() => import("../../MainAppView/Maintenances/MaintenancesPage")),
);
const RepairOperationCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/RepairOperations/RepairOperationCreateUpdatePage")),
);
const RepairOperationViewPage = Loadable(
  lazy(() => import("../../MainAppView/RepairOperations/RepairOperationViewPage")),
);
const RepairOperationsPage = Loadable(
  lazy(() => import("../../MainAppView/RepairOperations/RepairOperationsPage")),
);

export function OperationRoutes() {
  return (
    <>
      {/* VisualInspection */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.VisualInspectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.VISUAL_INSPECTIONS)}
        render={() => <VisualInspectionsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.VisualInspectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.VISUAL_INSPECTION_VIEW())}
        render={() => <VisualInspectionViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[
          AppPermission.VisualInspectionPerform,
          AppPermission.VisualInspectionManage,
        ]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.VISUAL_INSPECTION_CREATE())}
        render={() => <VisualInspectionCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.VisualInspectionManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.VISUAL_INSPECTION_EDIT())}
        render={() => <VisualInspectionCreateUpdatePage />}
      />

      {/* DamageDetection */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageDetectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTIONS)}
        render={() => <DamageDetectionsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageDetectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTION_VIEW())}
        render={() => <DamageDetectionViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.DamageDetectionPerform, AppPermission.DamageDetectionManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTION_CREATE())}
        render={() => <DamageDetectionCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageDetectionManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTION_EDIT())}
        render={() => <DamageDetectionCreateUpdatePage />}
      />

      {/* DamageDetectionAggregate */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageDetectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTION_AGGREGATES)}
        render={() => <DamageDetectionAggregatesPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageDetectionRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_DETECTION_AGGREGATE_VIEW())}
        render={() => <DamageDetectionAggregateViewPage />}
      />

      {/* DamageCostEvaluation */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageCostEvaluationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATIONS)}
        render={() => <DamageCostEvaluationsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageCostEvaluationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATION_VIEW())}
        render={() => <DamageCostEvaluationViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[
          AppPermission.DamageCostEvaluationPerform,
          AppPermission.DamageCostEvaluationManage,
        ]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATION_CREATE())}
        render={() => <DamageCostEvaluationCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageCostEvaluationManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATION_EDIT())}
        render={() => <DamageCostEvaluationCreateUpdatePage />}
      />

      {/* DamageCostEvaluationAggregate */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageCostEvaluationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATION_AGGREGATES)}
        render={() => <DamageCostEvaluationAggregatesPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.DamageCostEvaluationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DAMAGE_COST_EVALUATION_AGGREGATE_VIEW())}
        render={() => <DamageCostEvaluationAggregateViewPage />}
      />

      {/* AccessoryCheck */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.AccessoryCheckRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ACCESSORY_CHECKS)}
        render={() => <AccessoryChecksPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.AccessoryCheckRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ACCESSORY_CHECK_VIEW())}
        render={() => <AccessoryCheckViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.AccessoryCheckPerform, AppPermission.AccessoryCheckManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ACCESSORY_CHECK_CREATE())}
        render={() => <AccessoryCheckCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.AccessoryCheckManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ACCESSORY_CHECK_EDIT())}
        render={() => <AccessoryCheckCreateUpdatePage />}
      />

      {/* RepairOperation */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.RepairOperationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR_OPERATIONS)}
        render={() => <RepairOperationsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.RepairOperationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR_OPERATION_VIEW())}
        render={() => <RepairOperationViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.MaintenancePerform, AppPermission.RepairOperationManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR_OPERATION_CREATE())}
        render={() => <RepairOperationCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.RepairOperationManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR_OPERATION_EDIT())}
        render={() => <RepairOperationCreateUpdatePage />}
      />

      {/* WheelOperation */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WheelOperationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WHEEL_OPERATIONS)}
        render={() => <WheelOperationsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WheelOperationRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WHEEL_OPERATION_VIEW())}
        render={() => <WheelOperationViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.WheelOperationPerform, AppPermission.WheelOperationManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WHEEL_OPERATION_CREATE())}
        render={() => <WheelOperationCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WheelOperationManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WHEEL_OPERATION_EDIT())}
        render={() => <WheelOperationCreateUpdatePage />}
      />

      {/* Wash */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WashRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WASHES)}
        render={() => <WashesPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WashRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WASH_VIEW())}
        render={() => <WashViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.WashPerform, AppPermission.WashManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WASH_CREATE())}
        render={() => <WashCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.WashManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.WASH_EDIT())}
        render={() => <WashCreateUpdatePage />}
      />

      {/* Maintenance */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.MaintenanceRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MAINTENANCES)}
        render={() => <MaintenancesPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.MaintenanceRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MAINTENANCE_VIEW())}
        render={() => <MaintenanceViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        permissionsAny={[AppPermission.MaintenancePerform, AppPermission.MaintenanceManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MAINTENANCE_CREATE())}
        render={() => <MaintenanceCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.MaintenanceManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MAINTENANCE_EDIT())}
        render={() => <MaintenanceCreateUpdatePage />}
      />
    </>
  );
}
